import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import {
  Box,
  Button,
  Card,
  Modal,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Chip
} from "@mui/material";
import { DeleteSharp, EditSharp, RemoveRedEye, Close } from "@mui/icons-material";
import { getNotificationChildren } from "src/api/commonapi";
import CustomLoader from "src/components/CustomLoader";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "95%",
  maxWidth: 1100,
  maxHeight: "90vh",
  bgcolor: "background.paper",
  boxShadow: 10,
  borderRadius: "10px",
  p: 2,
  overflow: "auto"
};

const isEditable = (row) =>
  row.notification_send_status !== 1 && row.notification_send_status !== "1";

export default function ScheduleModal({
  open,
  onClose,
  parentRow,
  onEdit,
  onDelete,
  onViewRead
}) {
  const [children, setChildren] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open && parentRow?.n_id) fetchChildren(parentRow.n_id);
    if (!open) setChildren([]);
  }, [open, parentRow?.n_id]);

  const fetchChildren = async (nId) => {
    setIsLoading(true);
    try {
      const resp = await getNotificationChildren(nId);
      if (resp.status === 1) setChildren(resp.data || []);
    } catch (e) {
      console.error("Error fetching children:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    { name: "#", width: "50px", selector: (_, idx) => idx + 1 },
    {
      name: "Type",
      width: "70px",
      cell: (row) => (row.is_main === 1 || row.is_main === "1" ? "Main" : "Sub")
    },
    {
      name: "Send Type",
      width: "110px",
      cell: (row) =>
        String(row.notification_send_type) === "1" ? "Immediate" : "Scheduled"
    },
    { name: "Start Date", width: "110px", selector: (row) => row.s_date || "-" },
    { name: "End Date", width: "110px", selector: (row) => row.e_date || "-" },
    { name: "Send Date & Time", width: "180px", selector: (row) => row.n_date_time || "-" },
    {
      name: "Show on Home",
      width: "110px",
      cell: (row) =>
        row.show_on_home === 1 || row.show_on_home === "1" ? "Yes" : "No"
    },
    {
      name: "Send Status",
      width: "100px",
      cell: (row) => (
        <Chip
          label={String(row.notification_send_status) === "1" ? "Sent" : "Unsent"}
          color={String(row.notification_send_status) === "1" ? "success" : "default"}
          size="small"
        />
      )
    },
    {
      name: "Action",
      width: "120px",
      cell: (row) => {
        const editable = isEditable(row);
        return (
          <>
            <Tooltip title={!editable ? "Cannot delete sent notification" : "Delete"}>
              <span>
                <IconButton disabled={!editable} onClick={() => onDelete(row)} size="small">
                  <DeleteSharp
                    color={!editable ? "disabled" : "error"}
                    fontSize="small"
                  />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={!editable ? "Cannot edit sent notification" : "Edit"}>
              <span>
                <IconButton disabled={!editable} onClick={() => onEdit(row)} size="small">
                  <EditSharp
                    color={!editable ? "disabled" : "warning"}
                    fontSize="small"
                  />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="View Read Members">
              <IconButton onClick={() => onViewRead(row.n_id)} size="small">
                <RemoveRedEye color="success" fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        );
      }
    }
  ];

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="h6">
            Schedule Entries — {parentRow?.n_title || `#${parentRow?.n_id}`}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Stack>
        <hr />
        <Card sx={{ mt: 1 }}>
          <DataTable
            columns={columns}
            data={children}
            progressPending={isLoading}
            progressComponent={<CustomLoader />}
            noHeader
            highlightOnHover
            pagination
            paginationPerPage={10}
          />
        </Card>
        <div style={{ margin: "10px auto 0", display: "flex", justifyContent: "center" }}>
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
        </div>
      </Box>
    </Modal>
  );
}
